import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { Icon, Loader } from "semantic-ui-react";
import { getCurrentInspectionsInfolist, updateCurrentInspection } from "../../api/BorrowerApi";
import { getMockCurrentInspectionsInfolist, updateMockCurrentInspection } from "../../api/mock/MockBorrowerApi";
import { convertToDateString, formatDataProperties, handleErrorResponse } from "../../Helpers";
import { ICurrentInspectionsView } from "../interfaces/Borrower";
import { IErrorResponse, IProxyUser } from "../interfaces/Global";
import BCMenuBar from "../ui/BCMenuBar";
import BCMessage from "../ui/BCMessage";
import BCTable from "../ui/BCTable";
import BWExportButton from "./BWExportButton";
import BWSearchBox from "./BWSearchBox";
import "../../styles/borrower/vendor-form.scss";
import BCButton from "../ui/BCButton";
import BCFormInput from "../ui/BCFormInput";
import BCEmptyState from "../ui/BCEmptyState";
import { BorrowerConstants } from "../constants/BorrowerConstants";
import { FeatureFlags } from "../constants/LocalServerConstants";
import { handleApiPromise } from "../../helpers/GlobalHelper";

interface IBWCurrentInspections {
  isGreaterThanMobile: boolean;
  isGreaterThanTablet: boolean;
  isSidebar: boolean;
  isMessage: boolean;
  setIsMessage: React.Dispatch<React.SetStateAction<boolean>>;
  errorResponse: IErrorResponse | undefined;
  setErrorResponse: React.Dispatch<React.SetStateAction<IErrorResponse | undefined>>
  setSuccessMessage: React.Dispatch<React.SetStateAction<string>>; 
  user: any;
  proxyUser: IProxyUser | undefined;
}

const BWCurrentInspections = ({
  isGreaterThanMobile,
  isGreaterThanTablet,
  isSidebar,
  isMessage,
  setIsMessage,
  errorResponse,
  setErrorResponse,
  setSuccessMessage,
  user,
  proxyUser,
} : IBWCurrentInspections) => {
  const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
  const [isLoading, setIsLoading] = useState(true as boolean);
  const [currentInspectionsList, setCurrentInspectionsList] = useState([] as ICurrentInspectionsView[]);
  const [updatedCurrentInspection, setUpdatedCurrentInspection] = useState([] as any[]);

  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const EditScheduleDateOption = (inspection) => {
    const displayDate = inspection.scheduleDate ? convertToDateString(inspection.scheduleDate) : "Add";
    const handleSwitchToEditDateMode = () => {
      inspection.editMode = true;
      currentInspectionsList.forEach(x => {
        if (x.id !== inspection.id) {
          x.editMode = false;
        }

        return x;
      });

      setCurrentInspectionsList([...currentInspectionsList]);
    };

    const handleEditModeDateChange = async (value) => {
      inspection.newScheduleDate = value + "T00:00:00";
      setCurrentInspectionsList([...currentInspectionsList].map(x => { x.editMode = false; return x; }));

      const updatedInspection = updatedCurrentInspection.find(x => x.id === inspection.id);
      if (updatedInspection) {
        updatedInspection.scheduleDate = value + "T00:00:00";
      } else {
        const newUpdatedInspection = {
          "id": inspection.id,
          "nwccprojectId": inspection.nwccprojectId,
          "workInspectionCompanyOrganizationId": inspection.workInspectionCompanyOrganizationId, 
          "newScheduleDate": inspection.newScheduleDate
        };

        setUpdatedCurrentInspection([...updatedCurrentInspection, newUpdatedInspection]);
      }
    };

    return (
      <div>
        {!inspection.editMode || inspection.editMode === false ? 
          <div><Icon name="edit" onClick={() => handleSwitchToEditDateMode()} />
            {inspection.newScheduleDate ?
              <span className="changed-inspection-schedule-date">{convertToDateString(inspection.newScheduleDate)}</span> 
              : displayDate}
          </div>
          : <BCFormInput 
            inputCalendarClass="current-inspections-calendar"
            labelClass="gmr-background-label"
            value={convertToDateString(convertToDateString(inspection.scheduleDate), "calendar")}
            type="calendar"
            obj={updatedCurrentInspection}
            field="scheduleDate"
            handleFormChange={(value) =>  handleEditModeDateChange(value)}
            required
          />
        }
      </div>
    );
  };

  const updateCurrentInspectionAsync = async (inspection) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await updateMockCurrentInspection(inspection) : await updateCurrentInspection(inspection, proxyUser && proxyUser.id);
      if (response && response.ok) {
        setIsMessage(false);
        setSuccessMessage("Schedue Date has been updated");
        setTimeout(() => { 
          setSuccessMessage("");
        }, 5000);
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
  };

  const SaveScheduleDateButton = () => {
    const handleUpdateInspectionScheduleDate = async (updatedInspectionList) => {
      setIsLoading(true);
      updatedInspectionList.forEach(updatedInspection => {
        updatedInspection.updatedOn = new Date().toLocaleDateString();
        updatedInspection.updatedby = user.email;
        updatedInspection.ScheduleDate = updatedInspection.newScheduleDate;
        delete updatedInspection["newScheduleDate"];
        updateCurrentInspectionAsync(updatedInspection);
      });

      setIsLoading(false);
    };
    
    return (
      <BCButton handleOnClick={() => handleUpdateInspectionScheduleDate(updatedCurrentInspection)}>Save Schedule Date</BCButton>
    );
  };

  const CancelButton = () => {
    const handleCancelScheduleDateChange = () => {
      setIsLoading(true);
      const removedNewScheduleDateList = updatedCurrentInspection.map(x => {delete x["newScheduleDate"]; return x;});
      setUpdatedCurrentInspection(removedNewScheduleDateList);
      setIsLoading(false);
    };

    return (
      <BCButton handleOnClick={() => handleCancelScheduleDateChange()}>Cancel</BCButton>
    );
  };

  const propertyOfInspection = (name: keyof ICurrentInspectionsView) : string => name;
  const convertToTableFormat = (inspections : ICurrentInspectionsView[]) => {
    const result = [] as any;
    inspections.forEach((inspection) => {
      const resultItem = {} as any;
      Object.entries(inspection).forEach(([key, value]) => {
        if (key === propertyOfInspection("dateInspected") 
        || key === propertyOfInspection("completedOn") 
        || key === propertyOfInspection("createdOn") 
        || key === propertyOfInspection("updatedOn") 
        || key === propertyOfInspection("requestedDate")) {
          resultItem[key] = new Date(value).toLocaleDateString();
        }
        else if(!Object.values(BorrowerConstants.unUsedCurrentInspectionProperty).includes(key)){
          resultItem[key] = value;
        }
      });
      
      resultItem.loanNumber = <Link className="inspection-detail-link" to={`currentinspections/loan/${inspection.id}`}>{inspection.loanNumber}</Link>;
      resultItem.scheduleDate = EditScheduleDateOption(inspection);
      result.push(resultItem);
    });

    const formattedInspection = formatDataProperties(result, [
      {field: "tcname", action: "rename", name: "TC Name"}, 
      {field: "tcphoneNumber", action: "rename", name: "TC Phone Number"},
      {field: "dateInspected", action: "rename", name: "Inspected On"},
      {field: "loanNumber", action: "rename", name: "Loan #"},
    ]);
    return formattedInspection;
  };

  useEffect(() => {
    const getCurrentInspectionsListAysnc = async () => {
      const data = await handleApiPromise(
        () => getCurrentInspectionsInfolist(proxyUser && proxyUser.id),
        () => getMockCurrentInspectionsInfolist(),
        (response) => handleErrorResponse(response, setIsMessage, setErrorResponse)
      ) || [];

      const projectsViews = [] as ICurrentInspectionsView[];
      data.forEach((projectsInfolistItem) => {
        projectsViews.push(projectsInfolistItem as ICurrentInspectionsView);
      });

      setCurrentInspectionsList(projectsViews);
      setIsLoading(false);
    };

    if (isFirstLoad) {
      getCurrentInspectionsListAysnc();
    }
  }, [setIsMessage, setErrorResponse, setIsLoading, handleError, proxyUser, isFirstLoad]);

  useEffect(() => {
    setIsFirstLoad(false);
  }, []);

  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
      {isLoading ? <Loader active inline="centered" size="big" /> : <React.Fragment>
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={isMessage}
          transitionOnMount
          styles="full-width"
          onDismiss={() => setIsMessage(false)}
          errorResponse={errorResponse}
        />
        {currentInspectionsList.length > 0 ? <React.Fragment><BCMenuBar
          isGreaterThanMobile={isGreaterThanMobile}
          leftChildren={[<BWExportButton />, <SaveScheduleDateButton />, <CancelButton />]}
          rightChildren={[<BWSearchBox />]}
        />
        <BCTable
          dataRules={[{ field: "Id", rule: "hide" }, { field: "Edit Mode", rule: "hide" }]}
          data={convertToTableFormat(currentInspectionsList)}
          selectable
          overflowX
          overflowY
          dataSingleLine
          sortable={[
            "Loan Draw", 
            "Inspection Number", 
            "Inspection Status", 
            "Schedule Date", 
            "Inspected On", 
            "Completed On", 
            "Created On", 
            "Updated On",
            "Updated By",
            "Property",
            "Project Number",
            "Project Name",
            "Property Type",
            "Loan Number",
            "Requested Date", 
            "Borrower",
            "Builder",
            "Inspection Company"
          ]}
          pagination={17}
        /></React.Fragment>
        : <div><BCEmptyState
          emptyStateClass="borrower-portal-empty-state"
          icon="table"
          text="No Current Inspections to display"
        /></div>}
        {FeatureFlags.IS_DEBUG && <React.Fragment>
          <button onClick={() => console.log(currentInspectionsList)}>currentInspectionsList</button>
          <button onClick={() => console.log(updatedCurrentInspection)}>updatedCurrentInspection</button>
        </React.Fragment>}
      </React.Fragment>}
    </div>
  );
};

export default BWCurrentInspections;